import BaseClass from "../BaseClass";

export class PushSubscription extends BaseClass
{
    constructor(element) {
        super(element);

        // Abkürzungen für localStorage Keys
        this.localStorageKeys = {
            attempts: 'push_notifications_attempts',
            notification_last_attempt: 'push_notification_last_attempt',
            info_last_attempt: 'ios_info_last_attempt',
        };

        // Attempt Infos für Abfrage
        this.attemptSettings = {
            max_attempts: 4,
            attempt_interval_in_weeks: {
                0: 0,
                1: 1,
                2: 4,
                3: 12,
            }
        }
    }

    /**
     * Prüft, ob Push Notifications vom Browser unterstützt werden.
     *
     * @returns {boolean}
     */
    arePushNotificationsSupported() {
        return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    }

    /**
     * Prüft, ob der User um Erlaubnis gefragt werden darf.
     *
     * @returns {boolean}
     */
    canAskForPermission() {
        let attempts = this.getAttempts();
        let lastAttempt = localStorage.getItem(this.localStorageKeys.notification_last_attempt);
        let delayInWeeks = this.attemptSettings.attempt_interval_in_weeks[attempts] ?? 4;

        let hasSufficientDelay = this.isDelaySufficient(lastAttempt, delayInWeeks);

        return attempts <= 3 && hasSufficientDelay;
    }

    /**
     * Prüft, ob das Info-Modal für iOS-Geräte angezeigt werden darf.
     *
     * @returns {boolean}
     */
    canShowInfo() {
        let attempts = this.getAttempts();
        let lastAttempt = localStorage.getItem(this.localStorageKeys.info_last_attempt);
        let delayInWeeks = this.attemptSettings.attempt_interval_in_weeks[attempts] ?? 4;

        let hasSufficientDelay = this.isDelaySufficient(lastAttempt, delayInWeeks);

        return attempts <= 3 && hasSufficientDelay;
    }

    /**
     * Überprüft, ob die Zeitspanne zwischen dem letzten Versuch und dem jetzigen Zeitpunkt ausreichend ist.
     * Grundlage der Berechnungs ist die Dauer (delayInWeeks), die zwischen den Versuchen liegen muss.
     *
     * @param lastAttempt
     * @param delayInWeeks
     *
     * @returns {boolean}
     */
    isDelaySufficient(lastAttempt, delayInWeeks) {
        if (!lastAttempt) {
            return true;
        }

        const now = new Date();
        const lastDate = new Date(lastAttempt);
        const targetDate = new Date(lastDate);

        // Füge die Differenz in Tagen zum Zielzeitpunkt hinzu
        targetDate.setDate(lastDate.getDate() + delayInWeeks * 7);

        return now >= targetDate;
    }

    /**
     * Fragt die Erlaubnis für Push Notifications an.
     * Falls akzeptiert, wird eine Subscription erstellt und in die DB geschrieben.
     * Falls abgelehnt, wird dies im localStorage gespeichert.
     * Falls über das 'X' geschlossen wird, wird dies als attempt gezählt, und der TimeStamp entsprechend in der DB gesetzt.
     *
     * @returns {Promise<boolean>}
     */
    async askForNotificationPermission() {
        const permission = await Notification.requestPermission();

        if (permission === 'granted') {
            // Prepares subscription body for backend sending
            const subscription = await this.prepareSubscription();

            // Send subscription information to backend if subscription could be created.
            if (subscription) {
                await this.sendSubscriptionToServer(subscription);
            }

            // Setze permission als angenommen im localStorage
            await this.pushNotificationsAccepted();
            return true;
        } else if (permission === 'denied' || permission === 'default') {
            // Setze permission als abgelehnt im localStorage
            await this.pushNotificationsDeclined();
            return false;
        }
    }

    /**
     * Erstellt die Subscription und liefert die Infos, die in die DB geschrieben werden.
     *
     * @returns {Promise<{endpoint: string, key: string, token: string}|null>}
     */
    async prepareSubscription() {
        // Abbrechen, falls der Benutzer Benachrichtigungen nicht erlaubt hat
        if (Notification.permission !== 'granted') {
            return;
        }

        try {
            // Hole Public Vapid Key vom Server
            let vapidPublicKey = await this.getVapidPublicKey();

            // Beende den Vorgang, wenn kein Vapid Key abgerufen werden konnte
            if (!vapidPublicKey) {
                return null;
            }

            // Registriere den Service Worker
            const registration = await navigator.serviceWorker.getRegistration('/service-worker.js?v=1.0');

            // Keine gültige Subscription vorhanden: Jetzt neue Subscription generieren
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true, // Muss sichtbar für den Benutzer sein
                applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey),
            });

            const key = this.arrayBufferToBase64Url(subscription.getKey('p256dh'));
            const token = this.arrayBufferToBase64Url(subscription.getKey('auth'));
            const endpoint = subscription.endpoint;

            // Sende neue Subscription an den Server
            return {endpoint, key, token};
        } catch (error) {
            return null;
        }
    }

    /**
     * Schickt das übergebene push subscription object an den Server.
     *
     * @param {Object} subscription - Das push subscription object, welches an den Server gesendet wird. Beinhaltet den Endpunkt, Key und Token.
     *
     * @return {Promise<boolean>}   - Wird aufgelöst, sobald das Abonnement erfolgreich an den Server gesendet wurde.
     **/
    async sendSubscriptionToServer(subscription) {
        $.ajax({
            url: '/push-subscriptions',
            method: 'POST',
            data: JSON.stringify(subscription),
            contentType: 'application/json',
            headers: {
                // 'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content') // CSRF-Schutz
            },
            success: function() {
                return true;
            },
            error: function() {
                return false;
            }
        });
    }

    /**
     * Prüft, ob es sich beim aktuellen Client um ein Apple-Gerät handelt.
     *
     * @returns {boolean}
     */
    isAppleDevice() {
        // Match against possible Apple device values
        return /(iPhone|iPad|iPod|Mac)/.test(navigator.userAgent);
    }

    /**
     * Prüft, ob der display-mode standalone ist, was im Betrieb als PWA der Fall ist.
     *
     * @returns {boolean}
     */
    checkStandaloneMode() {
        // Überprüfe, ob die PWA im Standalone-Modus läuft
        return (window.matchMedia('(display-mode: standalone)').matches ||
            window.navigator.standalone === true || // iOS unterstützt navigator.standalone
            document.referrer.includes('android-app://'));
    }

    /**
     * Setzt ein Flag, wodurch der User nicht erneut nach einer Erlaubnis gefragt wird.
     *
     * @returns {Promise<boolean>}
     */
    pushNotificationsDeclined() {
        let now = new Date();

        localStorage.setItem(this.localStorageKeys.notification_last_attempt, now);

        let attempts = this.getAttempts() + 1;
        localStorage.setItem(this.localStorageKeys.attempts, attempts);
    }

    /**
     * Setzt ein Flag, damit der User nicht erneut gefragt wird, und einen TimeStamp mit dem Zeitpunkt der Zustimmung.
     *
     * @returns {Promise<boolean>}
     */
    pushNotificationsAccepted() {
        let now = new Date();

        localStorage.setItem(this.localStorageKeys.notification_last_attempt, now);

        let attempts = this.getAttempts() + 1;
        localStorage.setItem(this.localStorageKeys.attempts, attempts);
    }

    /**
     *
     */
    iosInfoDismissed() {
        let now = new Date();

        localStorage.setItem(this.localStorageKeys.info_last_attempt, now);

        let attempts = this.getAttempts() + 1;
        localStorage.setItem(this.localStorageKeys.attempts, attempts);
    }

    /**
     * Prüft, ob es im Backend eine Subscription zum übergebenen Endpoint gibt.
     *
     * @param endpoint
     * @returns {Promise<boolean>}
     */
    async checkPushSubscription(endpoint) {
        $.ajax({
            url: '/push-subscriptions/check/' + endpoint,
            method: 'GET',
            contentType: 'application/json',
            headers: {
                // 'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content') // CSRF-Schutz
            },
            success: function() {
                return true; // Erfolgreiche Antwort
            },
            error: function() {
                return false; // Fehlerhafte Antwort
            }
        });
    }

    /**
     *
     * @param base64String
     * @returns {Uint8Array}
     */
    urlBase64ToUint8Array(base64String) {
        // Fülle das Padding ('=') auf, falls die Länge des Base64-Strings nicht korrekt ist
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

        // Konvertiere Base64 in ein binäres String-Format
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        // Fülle das Uint8Array mit den entsprechenden Werten
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }

        return outputArray;
    }

    /**
     *
     * @param buffer
     * @returns {string}
     */
    arrayBufferToBase64Url(buffer) {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        bytes.forEach(byte => binary += String.fromCharCode(byte));
        return btoa(binary) // Base64 codieren
            .replace(/\+/g, '-') // Zu Base64URL anpassen
            .replace(/\//g, '_')
            .replace(/=+$/, ''); // Auffüllzeichen entfernen
    }

    /**
     * Liefert, falls vorhanden, den Endpunkt der aktiven Subscription, oder sonst null, zurück.
     *
     * @returns {*|null}
     */
    async getLocalPushSubscriptionEndpoint() {
        const registration = await navigator.serviceWorker.getRegistration('/service-worker.js?v=1.0');

        // Checke für eine bestehende Subscription, wenn der Service Worker registriert ist
        if (registration) {
            const subscription = await registration.pushManager.getSubscription();

            if (subscription != null) {
                return subscription.endpoint;
            }
        }

        return null;
    }

    /**
     * Überschreibt die user_id in der PushSubscription.
     * Stellt sicher, dass die aktive Subscription immer zum aktuellen User zugeordnet wird.
     */
    async overwriteUserForExistingEndpoint() {
        const subscription = await this.prepareSubscription();

        // Send subscription information to backend
        await this.sendSubscriptionToServer(subscription);
        return true;
    }

    /**
     * Registriert den ServiceWorker.
     *
     * @returns {Promise<void>}
     */
    async initiateServiceWorker() {
        await navigator.serviceWorker.register('/service-worker.js?v=1.0');
    }

    /**
     * Liefert den VAPID Public Key zurück.
     * Wird zur Erstellung der Subscription benötigt.
     *
     * @returns {Promise<*|null>}
     */
    async getVapidPublicKey() {
        const response = await fetch(('/push-subscriptions/vapid'), {
            method: 'GET',
            body: null,
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content') // CSRF-Schutz
            }
        });

        // Early exit falls etwas schiefgelaufen ist
        if (!response.ok) {
            return null;
        }

        const responseData = await response.json();

        return responseData.publicKey; // VAPID Public Key
    }

    /**
     * Hole die Attempt-Anzahl aus dem localStorage und parse sie als Number.
     *
     * @returns {number}
     */
    getAttempts() {
        return Number(localStorage.getItem(this.localStorageKeys.attempts)) ?? 0;
    }
}